import type { KycStatus } from "@/interfaces/kyc";
import type { AuthType, UserRole } from "@/interfaces/profile";
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface User {
  success: boolean;
  code: string;
  status?: string;
  userId?: string;
  isVerified?: boolean;
  message: string;
  kycStatus?: KycStatus;
  kycLevel?: number;
  twoFactorEnabled?: boolean;
  id: string;
  role: UserRole;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  avatar: string;
  phoneNumber: string;
  authType: AuthType;
  hasTransactionPassword: boolean;
}

export interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginChallenge: (state) => {
      state.isLoading = false;
      state.error = null;
    },

    loginRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    loginSuccess: (state, action: PayloadAction<User>) => {
      state.isLoading = false;
      state.user = action.payload;
      state.error = null;
    },

    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    clearAuth: (state) => {
      state.user = null;
      state.isLoading = false;
      state.error = null;
    },

    updateKycStatus: (
      state,
      action: PayloadAction<{ status: User["kycStatus"]; level: number }>,
    ) => {
      if (state.user) {
        state.user.kycStatus = action.payload.status;
        state.user.kycLevel = action.payload.level;
        localStorage.setItem("nexara_user", JSON.stringify(state.user));
      }
    },

    toggleTwoFactor: (state, action: PayloadAction<boolean>) => {
      if (state.user) {
        state.user.twoFactorEnabled = action.payload;
        localStorage.setItem("nexara_user", JSON.stringify(state.user));
      }
    },

    setSecurityCodeVerified: (state, action: PayloadAction<boolean>) => {
      if (state.user) {
        state.user.hasTransactionPassword = action.payload;
      }
    },
  },
});

export const {
  loginChallenge,
  loginRequest,
  loginSuccess,
  loginFailure,
  clearAuth,
  updateKycStatus,
  toggleTwoFactor,
  setSecurityCodeVerified,
} = authSlice.actions;
export default authSlice.reducer;
